/**
 * Single billing-recipient policy for the CRM UI and edge functions.
 * Pure: no imports, so `src/` can import it as well.
 *
 * Designated billing recipients win; otherwise the default contact is used.
 * - Designated: company channels typed "Billing", the company's billing
 *   contacts (`billing_contact_ids`), the linked contact's channels flagged
 *   `billing: true`, and the legacy invoice override.
 * - Default: linked contact → company primary contact → company channels.
 * Every value is read from the current records; nothing falls back to a
 * historical copy. A designated contact that no longer exists in the
 * account is reported as missing rather than replaced.
 */

export type PolicyEmailRow = {
  email?: string | null;
  type?: string | null;
  isPrimary?: boolean;
  billing?: boolean;
};

export type PolicyPhoneRow = {
  number?: string | null;
  type?: string | null;
  isPrimary?: boolean;
  billing?: boolean;
};

export type PolicyBillingContact = {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  email_jsonb?: PolicyEmailRow[] | null;
  phone_jsonb?: PolicyPhoneRow[] | null;
};

export type PolicyContact = {
  id?: number | string | null;
  email_jsonb?: PolicyEmailRow[] | null;
  phone_jsonb?: PolicyPhoneRow[] | null;
};

export type PolicyCompany = {
  context_links?: string[] | null;
  phone_number?: string | null;
  primary_contact_email_jsonb?: PolicyEmailRow[] | null;
  primary_contact_phone_jsonb?: PolicyPhoneRow[] | null;
  billing_contact_ids?: Array<number | string> | null;
  billing_contacts_jsonb?: PolicyBillingContact[] | null;
};

export type ClientBillingRecipients = {
  emails: string[];
  phones: string[];
  /** "billing" when designated recipients exist, else "default". */
  source: "billing" | "default";
  /** Designated contacts that were deleted or moved out of the account. */
  missingContactIds: Array<number | string>;
  /** True when no email can be resolved or a designated contact is gone. */
  missing: boolean;
};

const PREFIX = "lbs:";

const parseLink = (links: string[] | null | undefined, key: string) => {
  const prefix = `${PREFIX}${key}=`;
  const match = links?.find((link) => link.startsWith(prefix));
  return match ? match.slice(prefix.length) : undefined;
};

const parseRows = <T>(links: string[] | null | undefined, key: string): T[] => {
  const raw = parseLink(links, key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const rowsOf = <T>(rows: unknown): T[] =>
  Array.isArray(rows)
    ? (rows.filter((row) => row && typeof row === "object") as T[])
    : [];

const emailOf = (row: PolicyEmailRow) =>
  typeof row.email === "string" ? row.email.trim() : "";
const phoneOf = (row: PolicyPhoneRow) =>
  typeof row.number === "string" ? row.number.trim() : "";

const primaryValue = <T extends { isPrimary?: boolean }>(
  rows: T[],
  valueOf: (row: T) => string,
) => {
  const withValue = rows.filter((row) => valueOf(row));
  const row = withValue.find((entry) => entry.isPrimary) ?? withValue[0];
  return row ? [valueOf(row)] : [];
};

const flaggedValues = <T extends { billing?: boolean }>(
  rows: T[],
  valueOf: (row: T) => string,
) =>
  rows
    .filter((row) => row.billing === true)
    .map(valueOf)
    .filter(Boolean);

/** A contact's billing channels: rows flagged for billing, else its primary. */
export const contactBillingChannels = (contact?: PolicyContact | null) => {
  const emails = rowsOf<PolicyEmailRow>(contact?.email_jsonb);
  const phones = rowsOf<PolicyPhoneRow>(contact?.phone_jsonb);
  const flaggedEmails = flaggedValues(emails, emailOf);
  const flaggedPhones = flaggedValues(phones, phoneOf);
  return {
    emails: flaggedEmails.length
      ? flaggedEmails
      : primaryValue(emails, emailOf),
    phones: flaggedPhones.length
      ? flaggedPhones
      : primaryValue(phones, phoneOf),
  };
};

const unique = (values: string[], key: (value: string) => string) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const normalized = key(value);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const emailKey = (value: string) => value.trim().toLowerCase();
const phoneKey = (value: string) => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

export const resolveClientBillingRecipients = ({
  company,
  contact,
}: {
  company?: PolicyCompany | null;
  contact?: PolicyContact | null;
}): ClientBillingRecipients => {
  const designatedEmails: string[] = [];
  const designatedPhones: string[] = [];
  const missingContactIds: Array<number | string> = [];
  const links = company?.context_links ?? null;
  const designatedIds = (company?.billing_contact_ids ?? []).filter(
    (id) => id != null,
  );

  if (company) {
    const companyEmails = parseRows<PolicyEmailRow>(links, "company_emails");
    const companyPhones = parseRows<PolicyPhoneRow>(links, "company_phones");
    designatedEmails.push(
      ...companyEmails.filter((row) => row.type === "Billing").map(emailOf),
    );
    designatedPhones.push(
      ...companyPhones.filter((row) => row.type === "Billing").map(phoneOf),
    );

    const billingContacts = rowsOf<PolicyBillingContact>(
      company.billing_contacts_jsonb,
    );
    for (const id of designatedIds) {
      const billingContact = billingContacts.find(
        (row) => String(row.id) === String(id),
      );
      if (!billingContact) {
        missingContactIds.push(id);
        continue;
      }
      const channels = contactBillingChannels(billingContact);
      designatedEmails.push(...channels.emails);
      designatedPhones.push(...channels.phones);
    }

    // Legacy explicit invoice contact, kept until contacts are designated.
    if (
      !designatedIds.length &&
      parseLink(links, "invoice_same_as_primary") === "false"
    ) {
      designatedEmails.push(parseLink(links, "invoice_email")?.trim() ?? "");
      designatedPhones.push(parseLink(links, "invoice_phone")?.trim() ?? "");
    }
  }

  designatedEmails.push(
    ...flaggedValues(rowsOf<PolicyEmailRow>(contact?.email_jsonb), emailOf),
  );
  designatedPhones.push(
    ...flaggedValues(rowsOf<PolicyPhoneRow>(contact?.phone_jsonb), phoneOf),
  );

  const emails = unique(designatedEmails, emailKey);
  const phones = unique(designatedPhones, phoneKey);
  const hasDesignation =
    emails.length > 0 || phones.length > 0 || designatedIds.length > 0;

  if (hasDesignation) {
    return {
      emails,
      phones,
      source: "billing",
      missingContactIds,
      missing: emails.length === 0 || missingContactIds.length > 0,
    };
  }

  const fromContact = contactBillingChannels(contact);
  const fromPrimary = contactBillingChannels({
    email_jsonb: company?.primary_contact_email_jsonb,
    phone_jsonb: company?.primary_contact_phone_jsonb,
  });
  const companyEmail =
    primaryValue(
      parseRows<PolicyEmailRow>(links, "company_emails"),
      emailOf,
    )[0] ||
    parseLink(links, "business_email")?.trim() ||
    "";
  const companyPhone =
    primaryValue(
      parseRows<PolicyPhoneRow>(links, "company_phones"),
      phoneOf,
    )[0] ||
    company?.phone_number?.trim() ||
    "";

  const defaultEmails = fromContact.emails.length
    ? fromContact.emails
    : fromPrimary.emails.length
      ? fromPrimary.emails
      : companyEmail
        ? [companyEmail]
        : [];
  const defaultPhones = fromContact.phones.length
    ? fromContact.phones
    : fromPrimary.phones.length
      ? fromPrimary.phones
      : companyPhone
        ? [companyPhone]
        : [];

  return {
    emails: defaultEmails,
    phones: defaultPhones,
    source: "default",
    missingContactIds,
    missing: defaultEmails.length === 0,
  };
};
